// @ts-nocheck
/**
 * Catalogue natif (équivalent fonctionnel Calibre-Web dans l’app Biblio-Tech)
 *
 * UX : barre recherche + filtres (auteur, série, langue, format), tri, pagination serveur,
 * grille de cartes, panneau détail avec actions Lire (lecteur intégré) et Télécharger (fichier source).
 * Données : GET /api/books, facettes GET /api/books/filters/values
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  Loader,
  Library,
  Upload,
  SearchX,
} from 'lucide-react';
import { apiJSON } from '../config/api';
import { telechargerFichierApi } from '../utils/telechargementApi';
import { handleApiError } from '../utils/notificationService';
import BookCover from './BookCover';
import './CataloguePage.css';

const SORT_OPTIONS = [
  { value: 'title', label: 'Titre' },
  { value: 'author', label: 'Auteur' },
  { value: 'series', label: 'Série' },
  { value: 'year', label: 'Année' },
  { value: 'created_at', label: 'Ajout' },
  { value: 'lang', label: 'Langue' },
];

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  return q.toString();
}

export default function CataloguePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [facets, setFacets] = useState(null);
  const [selected, setSelected] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage] = useState(24);
  const [search, setSearch] = useState('');
  const [author, setAuthor] = useState('');
  const [series, setSeries] = useState('');
  const [language, setLanguage] = useState('');
  const [format, setFormat] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(true);

  const loadFacets = useCallback(async () => {
    try {
      const data = await apiJSON('books/filters/values');
      setFacets(data);
    } catch (e) {
      console.warn('Facettes indisponibles', e);
    }
  }, []);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQuery({
        page,
        per_page: perPage,
        search: search.trim() || undefined,
        author: author || undefined,
        series: series || undefined,
        language: language || undefined,
        format: format || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      const data = await apiJSON(`books?${qs}`);
      // `has_cover` était forcé à true pour contourner son absence de la réponse :
      // le schéma le supprimait. Il est désormais renvoyé, on lit la vraie valeur.
      setItems(data.books || []);
      setPagination(data.pagination || null);
    } catch (e) {
      setError(e.message || 'Erreur chargement');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, author, series, language, format, sortBy, sortOrder]);

  useEffect(() => {
    loadFacets();
  }, [loadFacets]);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  const totalPages = pagination?.total_pages ?? 1;

  // Un `<a href download>` vers l'API partait sans en-tête d'authentification :
  // sous auth, le navigateur enregistrait une erreur 401 au lieu du livre.
  const [telechargement, setTelechargement] = useState(false);
  const telecharger = useCallback(async () => {
    if (!selected?.book_id) return;
    setTelechargement(true);
    try {
      await telechargerFichierApi(
        `books/${selected.book_id}/file`,
        selected.file_original_name || `${selected.title || 'livre'}.${selected.file_type || 'bin'}`,
      );
    } catch (e) {
      handleApiError(e, 'Téléchargement du livre');
    } finally {
      setTelechargement(false);
    }
  }, [selected]);

  return (
    <div className="catalogue-page app-page">
      <header className="catalogue-page__header">
        <div className="catalogue-page__title-row">
          <Library size={28} aria-hidden />
          <div>
            <h1>Catalogue</h1>
            <p className="catalogue-page__subtitle">
              Parcourir et lire vos livres (même bibliothèque que Calibre) — sans quitter l’application.
            </p>
          </div>
        </div>
        <div className="catalogue-page__toolbar">
          <input
            type="search"
            className="catalogue-page__search"
            placeholder="Rechercher titre, auteur…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            aria-label="Recherche catalogue"
          />
          <button
            type="button"
            className="catalogue-page__filter-toggle"
            onClick={() => setShowFilters((s) => !s)}
          >
            <Filter size={20} /> Filtres
          </button>
        </div>
      </header>

      <div
        className={`catalogue-page__body${selected ? ' catalogue-page__body--detail' : ''}${showFilters ? '' : ' catalogue-page__body--no-filters'}`}
      >
        {showFilters && (
          <aside className="catalogue-page__filters" aria-label="Filtres">
            <label>
              Auteur
              <select value={author} onChange={(e) => { setAuthor(e.target.value); setPage(1); }}>
                <option value="">Tous</option>
                {(facets?.authors || []).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>
            <label>
              Série
              <select value={series} onChange={(e) => { setSeries(e.target.value); setPage(1); }}>
                <option value="">Toutes</option>
                {(facets?.series || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Langue
              <select value={language} onChange={(e) => { setLanguage(e.target.value); setPage(1); }}>
                <option value="">Toutes</option>
                {(facets?.languages || []).map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>
            <label>
              Format
              <select value={format} onChange={(e) => { setFormat(e.target.value); setPage(1); }}>
                <option value="">Tous</option>
                {(facets?.formats || []).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
            <label>
              Tri
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label>
              Ordre
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                <option value="asc">Croissant</option>
                <option value="desc">Décroissant</option>
              </select>
            </label>
          </aside>
        )}

        <div className="catalogue-page__main">
          {loading && (
            <div className="catalogue-page__loading">
              <Loader className="catalogue-page__spinner" size={24} />
              Chargement…
            </div>
          )}
          {error && <div className="catalogue-page__error" role="alert">{error}</div>}

          {!loading && !error && items.length > 0 && (
            <ul className="catalogue-page__grid">
              {items.map((book) => (
                <li key={book.book_id}>
                  <button
                    type="button"
                    className={`catalogue-card${selected?.book_id === book.book_id ? ' is-selected' : ''}`}
                    onClick={() => setSelected(book)}
                  >
                    <BookCover book={book} size="medium" />
                    <span className="catalogue-card__title">{book.title || book.file_original_name}</span>
                    <span className="catalogue-card__author">{book.author || '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* État vide : distingue "aucun résultat pour ces filtres" vs "bibliothèque vide" (AUD-008) */}
          {!loading && !error && items.length === 0 && (() => {
            const hasActiveFilters = Boolean(
              search.trim() || author || series || language || format
            );
            return (
              <div
                className="catalogue-page__empty"
                role="status"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '48px 24px',
                  gap: '12px',
                  color: 'var(--bt-text-muted, #6b7280)',
                }}
              >
                {hasActiveFilters ? (
                  <>
                    <SearchX size={40} aria-hidden />
                    <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--bt-text, #1f2937)' }}>
                      Aucun livre ne correspond à ces critères
                    </h2>
                    <p style={{ margin: 0, fontSize: '14px', maxWidth: '420px' }}>
                      Essayez d'élargir votre recherche ou de retirer un filtre.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setAuthor('');
                        setSeries('');
                        setLanguage('');
                        setFormat('');
                        setPage(1);
                      }}
                      className="catalogue-page__btn catalogue-page__btn--primary"
                      style={{ marginTop: '8px' }}
                    >
                      Effacer les filtres
                    </button>
                  </>
                ) : (
                  <>
                    <Library size={40} aria-hidden />
                    <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--bt-text, #1f2937)' }}>
                      Votre catalogue est vide
                    </h2>
                    <p style={{ margin: 0, fontSize: '14px', maxWidth: '420px' }}>
                      Importez vos premiers livres pour commencer à les explorer.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/upload')}
                      className="catalogue-page__btn catalogue-page__btn--primary"
                      style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Upload size={16} /> Importer un livre
                    </button>
                  </>
                )}
              </div>
            );
          })()}

          {pagination && totalPages > 1 && items.length > 0 && (
            <nav className="catalogue-page__pager" aria-label="Pagination">
              <button
                type="button"
                disabled={!pagination.has_prev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={20} /> Précédent
              </button>
              <span>
                Page {pagination.page} / {pagination.total_pages}
                {' '}
                ({pagination.total_items} livres)
              </span>
              <button
                type="button"
                disabled={!pagination.has_next}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant <ChevronRight size={20} />
              </button>
            </nav>
          )}
        </div>

        {selected && (
          <aside className="catalogue-page__detail" aria-label="Détail du livre">
            <h2>{selected.title || selected.file_original_name}</h2>
            <p className="catalogue-page__meta">{selected.author}</p>
            {selected.series && (
              <p className="catalogue-page__meta">Série : {selected.series}{selected.volume != null ? ` · tome ${selected.volume}` : ''}</p>
            )}
            <p className="catalogue-page__meta">{selected.lang} {selected.year ? `· ${selected.year}` : ''}</p>
            {selected.description && (
              <p className="catalogue-page__desc">{selected.description}</p>
            )}
            <div className="catalogue-page__actions">
              <button
                type="button"
                className="catalogue-page__btn catalogue-page__btn--primary"
                onClick={() => navigate(`/lecteur/${encodeURIComponent(selected.book_id)}`)}
              >
                <BookOpen size={20} /> Lire
              </button>
              <button
                type="button"
                className="catalogue-page__btn"
                onClick={telecharger}
                disabled={telechargement}
                aria-busy={telechargement}
              >
                <Download size={20} /> {telechargement ? 'Téléchargement…' : 'Télécharger'}
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
